import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import PieChart from 'react-minimal-pie-chart';

import AgGrid from '../../shared/agGrid/agGrid.component';
import Numeral, { NumeralTypes } from '../../shared/numeral.component';
import { getChartColor } from '../../shared/chartHelpers';
import Spinner from '../../shared/spinner.component';

import Routes from '../../routes';
import { fetchConnectionMonitorData, fetchHeartbeatMonitorStatus } from '../store/actions';
import { connectionsOverallStatus, connectionsMonitorDataLoading,
  selectHeartbeatStatus, selectHeartbeatStatusLoading } from '../store/selectors';
import { fetchConnections } from '../../shared/store/actions';
import { generateShowMoreRow, generateShowLessRow } from '../columnDefinitions';
import { NUMBER_OF_ITEMS_TO_DISPLAY_ON_WIDGET } from '../constants';

import columnDefinitions from '../../shared/columnDefinitions/connectionRunStatusWidget';
import heartbeatColumnDefinition from '@client-shared/columnDefinitions/heartbeatStatusWidget';

const getFormattedValue = value => <Numeral type={NumeralTypes.NUMBER}>{value}</Numeral>;

class ConnectionRunStatusWidget extends PureComponent {
  static propTypes = {
    connections: PropTypes.arrayOf(PropTypes.shape({
      connectionName: PropTypes.string.isRequired,
      scheduleStatus: PropTypes.number,
    }).isRequired).isRequired,
    fetchConnectionMonitorData: PropTypes.func.isRequired,
    fetchConnections: PropTypes.func.isRequired,
    fetchHeartbeatMonitorStatus: PropTypes.func.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    currentPeriod: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    overallStatus: PropTypes.shape({
      greenConnectionsCount: PropTypes.number.isRequired,
      totalConnectionsCount: PropTypes.number.isRequired,
      greenConnectionsPercents: PropTypes.number.isRequired,
    }).isRequired,
    isConnectionsMonitorDtaLoading: PropTypes.bool.isRequired,
    isHeartbeatMonitorStatusLoading: PropTypes.bool.isRequired,
    heartbeatStatus: PropTypes.arrayOf(PropTypes.shape({
      heartbeatStatus: PropTypes.number.isRequired,
    })).isRequired,
  };

  state = {
    showAll: false,
  };

  componentDidMount() {
    const { currentPeriod, fetchConnections, fetchConnectionMonitorData, fetchHeartbeatMonitorStatus } = this.props;

    fetchConnections(currentPeriod, {
      includeAllOption: false,
      setConnection: false,
    });
    fetchConnectionMonitorData(currentPeriod);
    fetchHeartbeatMonitorStatus();
  }

  toggleMore = () => {
    this.setState(({ showAll }) => ({ showAll: !showAll }));
  };

  showMoreRow = generateShowMoreRow({
    valueField: 'connectionName',
    onClick: this.toggleMore,
  });

  showLessRow = generateShowLessRow({
    valueField: 'connectionName',
    onClick: this.toggleMore,
  });


  navigateToConnectionRunStatus = () => {
    this.props.history.push(Routes.dashboard.CONNECTION_MONITOR);
  };

  getCollectionToShow = () => {
    const { connections } = this.props;
    const { showAll } = this.state;

    if (connections.length <= NUMBER_OF_ITEMS_TO_DISPLAY_ON_WIDGET || showAll) {
      return connections;
    }

    return connections.slice(0, NUMBER_OF_ITEMS_TO_DISPLAY_ON_WIDGET);
  };

  postProcessPopup = params => {
    if (params.type !== 'columnMenu') {
      return;
    }
    const ePopup = params.ePopup;
    ePopup.style.position = 'absolute';
    ePopup.style.minHeight = '281px';
  };

  getPinnedBottomRowData = () => {
    const { connections } = this.props;
    const { showAll } = this.state;

    if (connections.length <= NUMBER_OF_ITEMS_TO_DISPLAY_ON_WIDGET) {
      return null;
    }

    return showAll ? [this.showLessRow] : [this.showMoreRow];
  };

  render() {
    const { overallStatus, isConnectionsMonitorDtaLoading,
      heartbeatStatus, isHeartbeatMonitorStatusLoading } = this.props;
    const { greenConnectionsPercents, greenConnectionsCount, totalConnectionsCount } = overallStatus;

    const connectionsToShow = this.getCollectionToShow();

    return (
      <div className="widget connection-run-status-widget a-panel mb-3">
        <div className="card-header"><h5>Connection Run Status</h5></div>
        <div className="card-body">
          {
            isConnectionsMonitorDtaLoading && isHeartbeatMonitorStatusLoading
              ? <Spinner />
              : (
                <>
                  <div className="row">
                    <div className="d-none d-lg-block col-lg-3"/>
                    <div className="widget-heartbeat-status-container col-lg-9">
                      <AgGrid
                        columnDefs={heartbeatColumnDefinition}
                        domLayout="autoHeight"
                        rowData={heartbeatStatus}
                        showCount={false}
                        suppressRowClickSelection
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="widget-pie-chart-container d-none d-lg-block col-lg-3">
                      <div className="widget-pie-chart" onClick={this.navigateToConnectionRunStatus}>
                        <div className="widget-total-percentage">
                          {greenConnectionsPercents}%
                        </div>
                        <PieChart
                          data={[
                            { value: greenConnectionsPercents, key: 1, color: getChartColor(greenConnectionsPercents) },
                            { value: 100 - greenConnectionsPercents, key: 2, color: '#dee2e6' },
                          ]}
                          startAngle={-90}
                          lineWidth={15}
                          totalValue={100}
                        />
                      </div>
                      <div className="widget-status-counts">
                        {getFormattedValue(greenConnectionsCount)}{' '}
                        of{' '}
                        {getFormattedValue(totalConnectionsCount)}
                        <br />
                        <div className="widget-status-label">
                          On Schedule<br />
                          and<br />
                          Active<br />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-9">
                      <div>
                        <AgGrid
                          columnDefs={columnDefinitions}
                          domLayout="autoHeight"
                          rowData={connectionsToShow}
                          postProcessPopup={this.postProcessPopup}
                          pinnedBottomRowData={this.getPinnedBottomRowData()}
                          suppressRowClickSelection
                        />
                      </div>
                    </div>
                  </div>
                </>
              )
          }
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    connections: state.dashboard.connectionRunStatus.connectionsMonitorData,
    overallStatus: connectionsOverallStatus(state),
    isConnectionsMonitorDtaLoading: connectionsMonitorDataLoading(state),
    currentPeriod: state.shared.currentPeriod,
    heartbeatStatus: selectHeartbeatStatus(state),
    isHeartbeatMonitorStatusLoading: selectHeartbeatStatusLoading(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchConnectionMonitorData,
    fetchConnections,
    fetchHeartbeatMonitorStatus,
  }
)(withRouter(ConnectionRunStatusWidget));
